import type { AuthoredLevel, RescueLevel, ReferencePair, RescueEvent, Vec, Material } from './types';
import { emptyRecording } from './types';

export const CHAPTERS = ['搭桥入门', '弹力接力', '借力机关', '时差配合', '细心护送', '默契试炼'] as const;
const TITLES = [
  '一段小桥', '顺坡滑行', '落点接应', '绕开石墙', '双层平台', '桥与缓冲',
  '第一块弹板', '越过高台', '两次反弹', '低顶穿行', '弹后停稳', '分岔路线',
  '压下跷板', '轻重两端', '摆臂接力', '释放配重', '挡住回摆', '借力绕障',
  '为你开门', '等门再走', '先搭后擦', '上下轮流', '两道门廊', '借桥归还',
  '护送纸蛋', '落地缓冲', '避开碰撞', '同时按键', '双球分流', '双目标到站',
  '弹板与闸门', '配重与纸蛋', '撤桥与回程', '双球错峰', '三段接力', '最终团聚',
];
const pts = (coordinates: number[][]): Vec[] => coordinates.map(([x, y]) => ({ x, y }));
export const addLine = (id: string, points: number[][], material: Material = 'support', tick = 0): RescueEvent => ({ kind: 'add', tick, stroke: { id, material, points: pts(points) } });
type Route = { lead: number[][][]; follow: number[][][]; end: number[]; checkpoint: number[]; rails?: number[][][]; tip: string };
// 六种不同拓扑：贯通坡、折返坡、落点碗、窄顶通道、阶梯接力、长坡缓冲。
const ROUTES: Route[] = [
  { lead: [[[55, 260], [455, 445]]], follow: [[[430, 510], [920, 700]]], end: [850, 650], checkpoint: [400, 380, 220, 240], tip: '两段斜坡之间留一点落差，球才会顺着坡滑过去。' },
  { lead: [[[60, 240], [655, 410]]], follow: [[[790, 650], [120, 860]]], end: [185, 820], checkpoint: [580, 340, 230, 360], rails: [[[800, 260], [800, 720]]], tip: '先向右，再让墙挡住去势；橙色斜坡把球送回左边。' },
  { lead: [[[60, 250], [430, 480]]], follow: [[[360, 700], [550, 875], [820, 690]]], end: [550, 837], checkpoint: [350, 420, 390, 340], tip: '画一个宽口的碗，接住带着惯性落下的小球。' },
  { lead: [[[55, 240], [425, 405]]], follow: [[[420, 550], [940, 720]]], end: [875, 677], checkpoint: [365, 350, 230, 330], rails: [[[590, 455], [930, 570]], [[300, 650], [300, 900]]], tip: '上方有低矮的顶棚，平缓的路线更容易穿过去。' },
  { lead: [[[50, 250], [345, 345]], [[355, 390], [650, 490]]], follow: [[[600, 500], [970, 660]]], end: [915, 590], checkpoint: [555, 430, 260, 290], tip: '用两段阶梯接力，最后一段由好友补上。' },
  { lead: [[[55, 235], [360, 540]]], follow: [[[320, 680], [640, 800], [910, 730]]], end: [665, 775], checkpoint: [300, 465, 370, 270], tip: '坡越陡速度越快，用一个浅浅的弧形缓冲。' },
];

export function authoredLevel(number: number): AuthoredLevel {
  if (!Number.isInteger(number) || number < 1 || number > 36) throw new Error('正式关卡编号应为 1 到 36。');
  const chapter = Math.floor((number - 1) / 6); const slot = (number - 1) % 6;
  const route = ROUTES[slot];
  const level: RescueLevel = {
    id: 'rescue-' + String(number).padStart(3, '0'), version: 1, number, chapter: CHAPTERS[chapter], title: TITLES[number - 1],
    objective: '让小伙伴安全进入绿色终点，两个人的线条都要参与救援。',
    leadTask: '画出蓝色引路段，把小球送进交接区。', followTask: '接住好友送来的小球，画路送它进入绿色终点。',
    tip: route.tip, ticks: 1200, ink: { lead: 1500, follow: 1700 },
    areas: { lead: [{ x: 20, y: 30, w: 960, h: 530 }], follow: [{ x: 20, y: 480, w: 960, h: 500 }] },
    materials: chapter ? ['support', 'spring'] : ['support'],
    balls: [{ id: 'friend', position: { x: 130, y: 95 }, radius: 18, color: '#E58B48' }],
    rails: (route.rails ?? []).map((p, i) => ({ id: 'wall-' + i, points: pts(p) })),
    goals: [{ id: 'home', ball: 'friend', x: route.end[0] - 48, y: route.end[1] - 48, w: 96, h: 96, label: '到家' }],
    checkpoints: [{ ball: 'friend', x: route.checkpoint[0], y: route.checkpoint[1], w: route.checkpoint[2], h: route.checkpoint[3] }],
    switches: [], gates: [], levers: [], hazards: [], requiredSwitches: [], requiredLevers: [], requiredSprings: 0, requiredErase: false,
    fingerprint: 'campaign-' + number,
  };
  const lead = route.lead.map((p, i) => addLine('lead-' + i, p));
  const follow = route.follow.map((p, i) => addLine('follow-' + i, p));
  // 第二套配合先接住起点小球再擦掉托板，真正推迟物体的出发时间。
  const secondLead: RescueEvent[] = [addLine('waiting', [[85, 155], [190, 155]]), ...lead, { kind: 'erase', tick: 120, strokeId: 'waiting' }];
  const references: [ReferencePair, ReferencePair] = [
    { name: '顺势出发', lead: { version: 1, events: lead }, follow: { version: 1, events: follow } },
    { name: '托住再接力', lead: { version: 1, events: secondLead }, follow: { version: 1, events: follow.map(e => ({ ...e, tick: 120 })) } },
  ];
  if (chapter === 1) {
    // 弹板会改变碰撞反弹系数，要求实际发生弹性接触后才算完成。
    for (const ref of references) for (const e of ref.follow.events) if (e.kind === 'add') e.stroke = { ...e.stroke, material: 'spring' };
    level.requiredSprings = 1; level.followTask = '用弹性线承接小球，再让它进入绿色终点。';
    level.rails.push({ id: 'rebound-left', points: pts([[25, 220], [100, 950]]) }, { id: 'rebound-right', points: pts([[975, 200], [900, 950]]) });
    if (slot === 2) {
      for (const ref of references) ref.follow.events.push(addLine('second-bounce', [[80, 800], [470, 900]], 'spring', ref === references[1] ? 120 : 0));
      level.requiredSprings = 2; level.tip = '右边弹回来的球还会落向左边，再画一块弹板接力。';
    }
  }
  if (chapter === 2) {
    // 独立的左侧配重机关：玩家的蓝线把配重送上跷板；跷板倾斜后打开出口。
    level.balls.push({ id: 'weight', position: { x: 930, y: 80 }, radius: 22, density: 3, color: '#77889E', helper: true });
    level.levers.push({ id: 'balance', center: { x: 820, y: 390 }, width: 200, direction: -1, threshold: 0.035, limit: 0.35 });
    level.requiredLevers.push('balance');
    level.gates.push({ id: 'lever-door', x: level.goals[0].x - 10, y: level.goals[0].y - 30, w: 12, h: 140, switches: ['balance'] });
    level.leadTask = '引导小球交接，同时让灰色配重压下跷板，打开终点门。';
    for (const ref of references) ref.lead.events.unshift(addLine('weight-guide', [[970, 205], [860, 250]]));
    level.tip = '灰色配重压在跷板左侧时会打开门；小伙伴走另一条路。';
  }
  if (chapter === 3) {
    const cp = level.checkpoints[0];
    level.switches.push({ id: 'button', x: cp.x, y: cp.y, w: cp.w, h: cp.h, ball: 'friend', hold: slot === 1 ? 360 : 900 });
    level.requiredSwitches.push('button');
    level.gates.push({ id: 'door', x: level.goals[0].x - 12, y: level.goals[0].y - 80, w: 12, h: 180, switches: ['button'], delay: slot === 1 ? 120 : 0 });
    if (slot === 4) level.gates.push({ id: 'door-two', x: level.goals[0].x + 28, y: level.goals[0].y - 80, w: 12, h: 180, switches: ['button'], delay: 30 });
    if ([2, 3, 5].includes(slot)) {
      level.requiredErase = true;
      // 起点托板需要在游戏时间里撤掉；它会实际挡住小球出发。
      references[0].lead = { version: 1, events: [addLine('waiting', [[85, 155], [190, 155]]), ...lead, { kind: 'erase', tick: 60, strokeId: 'waiting' }] };
    }
    level.leadTask = '让小球触发蓝色开关，为好友打开通路。';
    level.followTask = '配合开门的时间，把小球接进终点。';
    level.tip = '先看好友何时开门；可以暂停规划，回退时双方一起回到同一时刻。';
  }
  if (chapter === 4) {
    if (slot < 3) {
      level.balls[0].fragile = 10; level.balls[0].color = '#E8C875';
      level.objective = '保护纸蛋安全到家，过猛的碰撞会把它撞破。';
      if (slot === 2) level.hazards.push({ x: 20, y: 920, w: 960, h: 60 });
    } else {
      level.balls.push({ ...level.balls[0], id: 'friend-two', position: { x: 180, y: 70 }, color: '#73AA97' });
      level.goals.push({ ...level.goals[0], id: 'home-two', ball: 'friend-two', label: '一起到家' });
      if (slot === 3) {
        const cp = level.checkpoints[0];
        for (const id of ['friend', 'friend-two']) level.switches.push({ id: 'switch-' + id, x: cp.x, y: cp.y, w: cp.w, h: cp.h, ball: id, hold: 600 });
        level.requiredSwitches = ['switch-friend', 'switch-friend-two']; level.simultaneous = true;
      }
      level.objective = '两位小伙伴都要安全到家。';
    }
  }
  if (chapter === 5) {
    const cp = level.checkpoints[0];
    level.switches.push({ id: 'relay-button', ...cp, hold: 900 });
    level.requiredSwitches.push('relay-button');
    level.gates.push({ id: 'relay-door', x: level.goals[0].x - 12, y: level.goals[0].y - 70, w: 12, h: 170, switches: ['relay-button'] });
    if (slot === 0) { level.requiredSprings = 1; for (const r of references) for (const e of r.follow.events) if (e.kind === 'add') e.stroke = { ...e.stroke, material: 'spring' }; }
    if (slot === 1) { level.balls[0].fragile = 11; level.balls[0].density = 2; }
    if (slot === 2 || slot === 4) { level.requiredErase = true; references[0].lead = { version: 1, events: [addLine('waiting', [[85, 155], [190, 155]]), ...lead, { kind: 'erase', tick: 90, strokeId: 'waiting' }] }; }
    if (slot === 3 || slot === 5) { level.balls.push({ ...level.balls[0], id: 'friend-two', position: { x: 180, y: 65 }, color: '#73AA97' }); level.goals.push({ ...level.goals[0], id: 'home-two', ball: 'friend-two', label: '团聚' }); }
    level.tip = '把前面学到的承接、等待和机关组合起来；先预览，再决定在哪一刻落笔。';
  }
  if (number === 9) {
    level.goals[0] = { ...level.goals[0], x: 470, y: 760, w: 180, h: 155 };
    level.hazards.push({ x: 80, y: 420, w: 140, h: 70 });
    level.leadTask = '把球引过左侧危险区，交给好友的两块弹板。';
  }
  if (number === 16) {
    level.requiredErase = true;
    for (const [i, ref] of references.entries()) ref.lead.events.push(addLine('weight-stop', [[885, 140], [975, 140]]), { tick: 75 + i * 120, kind: 'erase', strokeId: 'weight-stop' });
    level.tip = '先用托板接住配重，再在合适的时刻擦掉它，让跷板开门。';
  }
  if (number === 17) {
    for (const ref of references) ref.lead.events.push(addLine('lever-stop', [[708, 416], [790, 416]]));
    level.tip = '在跷板下面加一小段支撑，可以限制它的下摆幅度。';
  }
  if (number === 29) {
    level.goals[0].label = '橙球到站';
    level.goals[1] = { id: 'home-two', ball: 'friend-two', x: 740, y: 795, w: 105, h: 105, label: '绿球到站' };
    level.rails.push({ id: 'waiting-wall', points: pts([[980, 480], [980, 975]]) });
    for (const [i, ref] of references.entries()) ref.follow.events.push(addLine('lower-road', [[620, 930], [980, 790]], 'support', i * 120), { tick: 360 + i * 120, kind: 'erase', strokeId: 'follow-0' });
    level.requiredErase = true;
    level.followTask = '先送橙球到站，再撤掉上路，把绿球接到下面的终点。';
    level.tip = '两只球的终点不同；上路送走橙球后，绿球还需要另一段路。';
  }
  if (number === 30) {
    level.goals[0] = { ...level.goals[0], x: 588, y: 734, w: 57, h: 80, label: '橙球' };
    level.goals[1] = { ...level.goals[1], x: 664, y: 737, w: 65, h: 82, label: '绿球' };
  }
  if (number === 32) {
    level.balls.push({ id: 'weight', position: { x: 930, y: 80 }, radius: 22, density: 3, color: '#77889E', helper: true });
    level.levers.push({ id: 'balance', center: { x: 820, y: 390 }, width: 200, direction: -1, threshold: 0.035, limit: 0.35 });
    level.requiredLevers.push('balance');
    level.gates[0].switches.push('balance'); level.gates[0].all = true;
    for (const ref of references) ref.lead.events.push(addLine('weight-guide', [[970, 205], [860, 250]]));
    level.leadTask = '让配重压下跷板，同时把纸蛋送到交接区。';
  }
  if (number === 33) {
    level.areas.lead[0].h = 650;
    for (const [i, ref] of references.entries()) ref.lead.events.push(addLine('borrowed-bridge', [[340, 560], [340, 640], [750, 640], [750, 560]]), { tick: 240 + i * 120, kind: 'erase', strokeId: 'borrowed-bridge' });
    level.tip = '桥可以暂时托住小球，记得在录制里擦掉它，把下降的通路交给好友。';
  }
  for (const ref of references) {
    ref.lead.events.sort((a, b) => a.tick - b.tick);
    ref.follow.events.sort((a, b) => a.tick - b.tick);
  }
  // 单关对象与参考录制都独立，编辑某个关卡不会污染其他关或另一套配合。
  return JSON.parse(JSON.stringify({ level, references })) as AuthoredLevel;
}
export const campaign = () => Array.from({ length: 36 }, (_, i) => authoredLevel(i + 1).level);
export const referenceFor = (number: number, variant = 0) => authoredLevel(number).references[variant === 1 ? 1 : 0];
export const blankReference = (): ReferencePair => ({ name: '空白', lead: emptyRecording(), follow: emptyRecording() });
